/**
 * QL-140 — financial situation field applicability.
 *
 * Pure functions only (no db/env/logger imports), like the capacity module.
 * A financial situation model keeps one flat field list per category, each
 * field tagged with the client types it applies to (`fieldAppliesTo`). These
 * helpers turn those lists into "what may this client fill in", so the FS
 * service (authoritative save guard) and the AI tools (label resolution,
 * available-label listings) cannot drift apart.
 */

import type { FinancialFieldDefinition, FsClientType } from "./types/financial-situation-models";
import { fieldAppliesTo } from "./types/financial-situation-models";
import type { FinancialSituationInput } from "./types/flow-instances";
import { LIQUIDITY_BUFFER_MODES } from "./types/flow-instances";

/** The four field lists of a financial situation model, parsed. */
export type FsFieldLists = {
  incomeFields: FinancialFieldDefinition[];
  assetFields: FinancialFieldDefinition[];
  liabilityFields: FinancialFieldDefinition[];
  commitmentFields: FinancialFieldDefinition[];
};

/** The categories, in the order they are shown to the user. */
export type FsFieldCategory = "income" | "asset" | "liability" | "commitment";

/** Category → the `FsFieldLists` key and the `FinancialSituationInput` key. */
export const FS_CATEGORIES: {
  category: FsFieldCategory;
  fieldsKey: keyof FsFieldLists;
  entriesKey: "incomeEntries" | "assetEntries" | "liabilityEntries" | "commitmentEntries";
}[] = [
  { category: "income", fieldsKey: "incomeFields", entriesKey: "incomeEntries" },
  { category: "asset", fieldsKey: "assetFields", entriesKey: "assetEntries" },
  { category: "liability", fieldsKey: "liabilityFields", entriesKey: "liabilityEntries" },
  { category: "commitment", fieldsKey: "commitmentFields", entriesKey: "commitmentEntries" },
];

/**
 * QL-39/QL-330 — the keys a stored field definition may carry. Everything
 * else (the pre-QL-39 `type`, `helpText`, `helpTextI18n`, and anything a
 * client invents) is dropped on read and on write, so a model row written
 * before QL-39 is cleaned the first time it is saved again and never leaks
 * the removed keys through an API.
 */
export function sanitizeFieldDefinition(raw: unknown): FinancialFieldDefinition | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const f = raw as Record<string, unknown>;
  if (typeof f.id !== "string" || f.id.length === 0) return null;
  const field: FinancialFieldDefinition = {
    id: f.id,
    label: typeof f.label === "string" ? f.label : "",
  };
  if (f.labelI18n && typeof f.labelI18n === "object" && !Array.isArray(f.labelI18n)) {
    field.labelI18n = f.labelI18n as Record<string, string>;
  }
  if (f.required === true) field.required = true;
  if (typeof f.hasMonthlyPayment === "boolean") field.hasMonthlyPayment = f.hasMonthlyPayment;
  if (Array.isArray(f.clientTypes)) {
    const types = f.clientTypes.filter(
      (t): t is FsClientType => t === "Person" || t === "Company" || t === "Group",
    );
    if (types.length > 0) field.clientTypes = types;
  }
  const syncKey = normalizeSyncKey(f.syncKey);
  if (syncKey) field.syncKey = syncKey;
  return field;
}

/**
 * QL-586 — the one display rule for a field label: the locale's own text, else
 * the typed label. No cross-locale fallback. Callers append their own last
 * resort (`|| field.id`, `|| entry.typeLabel`).
 */
export function resolveFieldLabel(
  field: Pick<FinancialFieldDefinition, "label" | "labelI18n">,
  lang: string,
): string {
  const own = field.labelI18n?.[lang];
  if (typeof own === "string" && own.trim()) return own;
  return field.label ?? "";
}

/**
 * QL-586 — merge a saved translation dialog into a field. The dialog is shown
 * the designer-language text (`resolveFieldLabel`) and echoes it back, so a
 * save that only added another language must change neither `label` nor the
 * stored entry for the designer's language. Only a real edit of the
 * designer-language text updates `label` (= the last text typed in that
 * language) and that entry.
 */
export function applyTranslationDialog(
  field: Pick<FinancialFieldDefinition, "label" | "labelI18n">,
  translations: Record<string, string>,
  lang: string,
): { label: string; labelI18n: Record<string, string> } {
  const shown = resolveFieldLabel(field, lang);
  const saved = translations[lang];
  const merged = { ...translations };
  if (saved === shown) {
    const before = field.labelI18n?.[lang];
    if (before === undefined) delete merged[lang];
    else merged[lang] = before;
    return { label: field.label, labelI18n: merged };
  }
  return { label: saved?.trim() || field.label, labelI18n: merged };
}

/** Every element of a raw list that is a usable field definition, sanitized. */
export function sanitizeFieldList(raw: unknown): FinancialFieldDefinition[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(sanitizeFieldDefinition).filter((f): f is FinancialFieldDefinition => f !== null);
}

/**
 * Parse one of the model's JSON-as-text field columns. Anything malformed
 * yields an empty list — the same conservatism the completion loader and the
 * wizard apply. Every field comes back sanitized (QL-39).
 */
export function parseFieldList(raw: unknown): FinancialFieldDefinition[] {
  if (Array.isArray(raw)) return sanitizeFieldList(raw);
  if (typeof raw !== "string") return [];
  try {
    return sanitizeFieldList(JSON.parse(raw));
  } catch {
    return [];
  }
}

// ─── Sync keys (QL-330) ──────────────────────────────────────────────────────

/**
 * QL-330 — a field's sync key as stored and compared: trimmed, case-sensitive.
 * Empty (or not a string) means "no key". Same rule as `wrapper_type.syncKey`.
 */
export function normalizeSyncKey(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const key = raw.trim();
  return key.length > 0 ? key : undefined;
}

/** One sync key used by two or more fields that apply to the same client type. */
export type DuplicateSyncKey = { syncKey: string; clientType: FsClientType };

const CLIENT_TYPES: readonly FsClientType[] = ["Person", "Company", "Group"];

/**
 * QL-330 — the sync keys that collide. A key must be unique among the fields
 * that apply to one client type, across all four categories; the same key on
 * a Person field and a Company field is fine (an integration writes one
 * category regardless of client type, and the client's type picks the field).
 * The editor runs this inline and the model service runs it on write.
 */
export function findDuplicateSyncKeys(lists: FsFieldLists): DuplicateSyncKey[] {
  const all = [
    ...lists.incomeFields,
    ...lists.assetFields,
    ...lists.liabilityFields,
    ...lists.commitmentFields,
  ];
  const found: DuplicateSyncKey[] = [];
  for (const clientType of CLIENT_TYPES) {
    const seen = new Set<string>();
    const reported = new Set<string>();
    for (const field of all) {
      const key = normalizeSyncKey(field.syncKey);
      if (!key || !fieldAppliesTo(field, clientType)) continue;
      if (seen.has(key) && !reported.has(key)) {
        found.push({ syncKey: key, clientType });
        reported.add(key);
      }
      seen.add(key);
    }
  }
  return found;
}

/**
 * One entry whose field could not be resolved. The API turns the reason into a
 * translated message (QL-718), so no text is written here.
 */
export type UnresolvedSyncKey =
  | {
      /** JSON path of the offending entry, e.g. `assetEntries[2]`. */
      path: string;
      /** The entry carries neither a `typeId` nor a `typeSyncKey`. */
      reason: "missing_type";
    }
  | {
      /** JSON path of the offending key, e.g. `assetEntries[2].typeSyncKey`. */
      path: string;
      /** No field of the client's type in this category has the key. */
      reason: "unknown_sync_key";
      key: string;
      clientType: FsClientType;
    };

/** One entry whose `amount` is present but not an entered number. */
export type InvalidEntryAmount = {
  /** JSON path of the amount, e.g. `incomeEntries[2].amount`. */
  path: string;
  /** The value as sent. */
  amount: unknown;
};

/**
 * QL-330 — resolve `typeSyncKey` on every entry to the field id the key names.
 *
 * Each entry must carry a `typeId` or a `typeSyncKey`. A key is looked up
 * among the fields of the ENTRY'S OWN category that apply to the client's
 * type — an asset entry cannot name an income field, and a Person client
 * cannot name a Company field even when the key exists there. The resolved
 * input sets `typeId` and carries the field's key as `typeSyncKey` on every
 * row whose field has one; the existing applicability guard then runs on
 * `typeId` exactly as before.
 *
 * Returns the resolved input, or the failures (nothing is resolved partially).
 */
export function resolveEntryTypeIds(
  input: FinancialSituationInput,
  modelLists: FsFieldLists,
  clientType: FsClientType,
):
  | { input: FinancialSituationInput; errors?: undefined }
  | { input?: undefined; errors: UnresolvedSyncKey[] } {
  const errors: UnresolvedSyncKey[] = [];
  const resolved: FinancialSituationInput = { ...input };

  for (const { fieldsKey, entriesKey } of FS_CATEGORIES) {
    const entries = input[entriesKey] ?? [];
    if (entries.length === 0) continue;
    const byKey = new Map<string, string>();
    for (const field of modelLists[fieldsKey]) {
      const key = normalizeSyncKey(field.syncKey);
      if (key && fieldAppliesTo(field, clientType)) byKey.set(key, field.id);
    }
    const keyById = new Map<string, string>();
    for (const [key, id] of byKey) keyById.set(id, key);
    resolved[entriesKey] = entries.map((entry, index) => {
      const hasId = typeof entry.typeId === "string" && entry.typeId.length > 0;
      const key = normalizeSyncKey(entry.typeSyncKey);
      if (!hasId && !key) {
        errors.push({
          path: `${entriesKey}[${index}]`,
          reason: "missing_type",
        });
        return entry;
      }
      // `typeId` wins when both are present (the advisor may have re-pointed a
      // row the integration wrote); the key is re-derived from the field so
      // the stored row never carries a stale one. The public route rejects
      // both-at-once before reaching here.
      if (hasId) {
        const ownKey = keyById.get(entry.typeId);
        if (ownKey) return { ...entry, typeSyncKey: ownKey };
        if (entry.typeSyncKey === undefined) return entry;
        const { typeSyncKey: _dropped, ...rest } = entry;
        return rest;
      }
      const typeId = byKey.get(key as string);
      if (!typeId) {
        errors.push({
          path: `${entriesKey}[${index}].typeSyncKey`,
          reason: "unknown_sync_key",
          key: key as string,
          clientType,
        });
        return entry;
      }
      return { ...entry, typeId, typeSyncKey: key };
    });
  }

  return errors.length > 0 ? { errors } : { input: resolved };
}

// ─── Required fields (QL-39) ─────────────────────────────────────────────────

/**
 * QL-368 — whether an entry carries an amount the user actually entered.
 * `0` does; an absent, null or non-numeric amount does not. Shared by the
 * completion predicate and the widget so "filled" means one thing.
 */
export function hasEnteredAmount(amount: unknown): amount is number {
  return typeof amount === "number" && Number.isFinite(amount);
}

/**
 * QL-368 — the entries whose `amount` is present but not an entered number
 * (null, a string, NaN). An absent amount is fine — it means "not given yet" —
 * but a present non-number must be refused rather than coerced, or a caller
 * could land a row that reads as an entered 0 and unlocks a required field.
 * The save path runs this so the web, the AI tool and the public API share it.
 */
export function findInvalidEntryAmounts(
  input: Pick<
    FinancialSituationInput,
    "incomeEntries" | "assetEntries" | "liabilityEntries" | "commitmentEntries"
  >,
): InvalidEntryAmount[] {
  const errors: InvalidEntryAmount[] = [];
  for (const { entriesKey } of FS_CATEGORIES) {
    (input[entriesKey] ?? []).forEach((entry, index) => {
      const amount = (entry as { amount?: unknown })?.amount;
      if (amount !== undefined && !hasEnteredAmount(amount)) {
        errors.push({
          path: `${entriesKey}[${index}].amount`,
          amount,
        });
      }
    });
  }
  return errors;
}

/**
 * QL-127 — the buffer mode is an enum the engine branches on. An unknown value
 * would be stored as sent while the engine silently re-infers the mode from
 * the buffer, so a present value outside the two literals is refused. The
 * public API's schema rejects it first; the save path runs this so the
 * internal route and the AI tool share the rule.
 */
export function findInvalidLiquidityBufferMode(
  input: Pick<FinancialSituationInput, "liquidityBufferMode">,
): { path: "liquidityBufferMode"; mode: unknown } | null {
  const mode = input.liquidityBufferMode;
  if (mode === undefined || LIQUIDITY_BUFFER_MODES.includes(mode)) return null;
  return { path: "liquidityBufferMode", mode };
}

/**
 * QL-39 — the ids of the fields marked required that apply to this client
 * type, across all four categories, in model order.
 */
export function requiredFieldIdsFor(lists: FsFieldLists, clientType: FsClientType): string[] {
  const ids: string[] = [];
  for (const { fieldsKey } of FS_CATEGORIES) {
    for (const field of lists[fieldsKey]) {
      if (field.required === true && fieldAppliesTo(field, clientType)) ids.push(field.id);
    }
  }
  return ids;
}

/**
 * QL-39 — the required field ids that have no entry with an entered amount.
 * Reads the raw input across all four entry lists. QL-368: any finite number
 * counts, `0` included — a row the widget seeded carries no `amount` at all,
 * and that is the only shape that does not count. The completion engine and
 * the widget's markers both use this so they can never disagree.
 */
export function missingRequiredFieldIds(
  input: Pick<
    FinancialSituationInput,
    "incomeEntries" | "assetEntries" | "liabilityEntries" | "commitmentEntries"
  > | null,
  requiredIds: readonly string[],
): string[] {
  if (requiredIds.length === 0) return [];
  const filled = new Set<string>();
  for (const { entriesKey } of FS_CATEGORIES) {
    for (const entry of input?.[entriesKey] ?? []) {
      if (hasEnteredAmount(entry?.amount)) filled.add(entry.typeId);
    }
  }
  return requiredIds.filter((id) => !filled.has(id));
}

/** Every list narrowed to the fields this client type may fill in. */
export function applicableFieldsFor(lists: FsFieldLists, clientType: FsClientType): FsFieldLists {
  return {
    incomeFields: lists.incomeFields.filter((f) => fieldAppliesTo(f, clientType)),
    assetFields: lists.assetFields.filter((f) => fieldAppliesTo(f, clientType)),
    liabilityFields: lists.liabilityFields.filter((f) => fieldAppliesTo(f, clientType)),
    commitmentFields: lists.commitmentFields.filter((f) => fieldAppliesTo(f, clientType)),
  };
}

/** One entry that points at a field the client's type may not fill in. */
export type InapplicableEntry = {
  category: FsFieldCategory;
  typeId: string;
  /** The model's label for the id, when the id is a field of the model at all. */
  label?: string;
};

/**
 * The entries whose `typeId` is not an applicable field of `clientType`.
 * Covers both an id belonging to another type's field and an id the model does
 * not know at all (today both are silently summed into the totals).
 *
 * A category whose list in the MODEL is empty is left unconstrained: the
 * advisor wizard then falls back to the component config's field copies and
 * finally to its built-in defaults, neither of which the server can see.
 */
export function findInapplicableEntries(
  input: FinancialSituationInput,
  modelLists: FsFieldLists,
  clientType: FsClientType,
): InapplicableEntry[] {
  const found: InapplicableEntry[] = [];

  for (const { category, fieldsKey, entriesKey } of FS_CATEGORIES) {
    const fields = modelLists[fieldsKey];
    if (fields.length === 0) continue;

    const entries = input[entriesKey] ?? [];
    if (entries.length === 0) continue;

    const allowed = new Set(fields.filter((f) => fieldAppliesTo(f, clientType)).map((f) => f.id));
    for (const entry of entries) {
      if (allowed.has(entry.typeId)) continue;
      const known = fields.find((f) => f.id === entry.typeId);
      found.push({
        category,
        typeId: entry.typeId,
        ...(known?.label ? { label: known.label } : {}),
      });
    }
  }

  return found;
}

// ─── Flow currency (QL-298) ──────────────────────────────────────────────────

/**
 * QL-298 — the financial situation is denominated in the FLOW currency:
 * `calculatedData` totals are in it, the synced accounts are converted into
 * it, and the entered rows are taken as already being in it — they are never
 * converted. An input that claims another currency would therefore be summed
 * as if it were the flow currency, so the save rejects it.
 *
 * Returns the expected (flow) currency, upper-cased, when the input claims
 * another one — the API words the refusal (QL-718). Returns null when the
 * input is acceptable: an
 * omitted/blank currency (the caller gets the flow currency) or one equal to
 * it, compared case-insensitively and ignoring surrounding whitespace.
 */
export function flowCurrencyMismatch(
  inputCurrency: string | null | undefined,
  flowCurrency: string,
): string | null {
  const given = inputCurrency?.trim();
  if (!given) return null;
  if (given.toUpperCase() === flowCurrency.trim().toUpperCase()) return null;
  return flowCurrency.toUpperCase();
}
